import merge from 'lodash.merge';
import upperfirst from 'lodash.upperfirst';
import { FC, memo, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
// mui components
import { Avatar, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
// components
import DefaultAvatar from 'components/default-avatar';
// custom hooks
import { useSidePanel } from 'hooks/useSidePanel';
// api
import { IProjectProfiles, IProjectsProfiles } from 'redux-store/api/projects';
import { IStyles } from 'redux-store/types';
// helpers
import { getImageBySize } from 'utils/helpers';
// icons
import CommentIcon from 'assets/icons/buttons/comment-icon.svg';
import MailboxIcon from 'assets/icons/buttons/email.svg';
import PhoneIcon from 'assets/icons/buttons/phone.svg';
// styles
import AdminIcon from 'assets/icons/buttons/admin.svg';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { styles } from './styles';

interface IUserCardProps {
  profile: IProjectProfiles & { isAllowedSelectContact: boolean };
  projectProfiles?: IProjectsProfiles;
  customStyles?: IStyles;
  closeModal: () => void;
  onContactPersonSelected: (profile: IProjectProfiles) => void;
  selectedContactPersonId?: number;
  loading: boolean;
}

const UserCard: FC<IUserCardProps> = ({
  profile,
  projectProfiles,
  customStyles,
  closeModal,
  onContactPersonSelected,
  selectedContactPersonId,
  loading,
}) => {
  const navigate = useNavigate();

  const {
    onSetMentionedProfile,
    handleExpandeEditor,
    isSidePanelOpen,
    handleOpenActivityFeed,
    projectId,
  } = useSidePanel();

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const { data: userMe } = useGetMeQuery();

  const initials = `${profile.firstName[0]}${profile.lastName[0]}`;

  const containerStyles = customStyles ? merge(styles.container, customStyles) : styles.container;

  const onMessageIconClick = () => {
    if (window.location.pathname.includes('project-dashboard')) {
      onSetMentionedProfile(profile);
      if (!isSidePanelOpen) {
        handleOpenActivityFeed();
      }
      handleExpandeEditor(true);
      closeModal();
    } else {
      onSetMentionedProfile(profile);
      if (!isSidePanelOpen) {
        handleOpenActivityFeed();
      }
      handleExpandeEditor(true);
      navigate(`/project-dashboard/${projectId}`);
    }
  };

  const onContactPersonClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (profile.isAllowedSelectContact && !projectProfiles?.isContactPerson) {
      onContactPersonSelected(profile);
    }
  };

  return (
    <Grid
      sx={{
        ...containerStyles,
        borderColor: projectProfiles?.isContactPerson ? '#1888dd' : '#D0D1D9',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {loading && profile.isAllowedSelectContact && selectedContactPersonId === profile.id && (
        <Grid
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <CircularProgress size={32} />
        </Grid>
      )}
      <Grid
        sx={{
          opacity:
            loading && profile.isAllowedSelectContact && !projectProfiles?.isContactPerson
              ? 0.5
              : 1,
          pointerEvents:
            loading && profile.isAllowedSelectContact && !projectProfiles?.isContactPerson
              ? 'none'
              : 'auto',
        }}
      >
        <Grid sx={styles.firstSectionContainer}>
          <Grid sx={styles.avatarContainer}>
            {profile?.avatar ? (
              <Avatar
                alt="avatar"
                sx={styles.avatar}
                src={getImageBySize(profile.avatar, 40, 40)}
                variant="square"
              />
            ) : (
              <DefaultAvatar initials={initials} variant="square" sx={styles.avatar} />
            )}
            <Grid sx={styles.nameContainer}>
              <Typography sx={styles.name}>{`${profile.firstName} ${profile.lastName}`}</Typography>
              <Typography sx={styles.role}>
                {upperfirst(profile?.role?.toLowerCase() ?? '')}
              </Typography>
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                lineHeight: 0,
                '&:hover': {
                  cursor:
                    profile.isAllowedSelectContact && !projectProfiles?.isContactPerson
                      ? 'pointer'
                      : 'default',
                },
              }}
              onClick={onContactPersonClick}
            >
              <AdminIcon
                color="#000000"
                fill={projectProfiles?.isContactPerson ? '#FFD700' : 'none'}
              />
            </Grid>
          </Grid>
          {userMe?.id !== profile?.id && userPermissions?.ENABLE_ACTIVITY_FEED?.canCreate ? (
            <IconButton onClick={onMessageIconClick} sx={styles.messageIcon}>
              <CommentIcon />
            </IconButton>
          ) : null}
        </Grid>
        <Grid sx={styles.contactsContainer}>
          <Grid sx={styles.contactSectionContainer}>
            <PhoneIcon />
            <Typography sx={styles.userContact}>{profile.phone ? profile.phone : '-'}</Typography>
          </Grid>
          <Grid sx={styles.contactSectionContainer}>
            <MailboxIcon />
            <Typography sx={styles.userContact}>{profile.email}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo(UserCard);
